/**
 * Live game clock and connection state hooks.
 *
 * The server sends `now` with each view; between views the clock advances locally from
 * `startedAtMs` and `timeScale` (corrected by the server clock offset from heartbeats).
 */
import { useMemo, useSyncExternalStore } from 'react';
import { realToGameTime, type GameTime, type PlayerView } from '@seven-dawns/shared';
import { createTimeFormat, type TimeFormat } from '../lib/time';
import { useMatchStore } from './matchStore';

// One shared 1 s ticker for every subscriber.
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let nowMs = Date.now();

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!timer) {
    nowMs = Date.now();
    timer = setInterval(() => {
      nowMs = Date.now();
      for (const l of listeners) l();
    }, 1000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** Wall-clock milliseconds, re-rendering every second. */
export function useNowMs(): number {
  return useSyncExternalStore(
    subscribe,
    () => nowMs,
    () => nowMs,
  );
}

export function gameNowFor(view: PlayerView, wallMs: number, clockOffsetMs: number): GameTime {
  if (view.status !== 'running' || view.startedAtMs === null) return view.now;
  const live = realToGameTime(wallMs + clockOffsetMs, view.startedAtMs, view.config);
  return Math.min(view.endsAt, Math.max(view.now, live));
}

/** Current game time for the loaded match (minutes since start). */
export function useGameNow(): GameTime {
  const view = useMatchStore((s) => s.view);
  const offset = useMatchStore((s) => s.clockOffsetMs);
  const wall = useNowMs();
  return view ? gameNowFor(view, wall, offset) : 0;
}

/** The match's time formatter (wall clock for real-time matches, in-world clock when sped up). */
export function useTimeFormat(): TimeFormat {
  const config = useMatchStore((s) => s.view?.config ?? s.lobby?.summary.config ?? null);
  const startedAtMs = useMatchStore((s) => s.view?.startedAtMs ?? null);
  return useMemo(
    () => createTimeFormat({ config: config ?? { timeScale: 1, durationDays: 7 }, startedAtMs }),
    [config, startedAtMs],
  );
}

export type ConnectionState = 'online' | 'connecting' | 'reconnecting' | 'offline';

const OFFLINE_AFTER_MS = 30_000;

function subscribeOnline(onChange: () => void) {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

export function useBrowserOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );
}

/** §9.4: reconnecting for < 30 s, offline after 30 s or when the browser reports offline. */
export function useConnectionState(): ConnectionState {
  const status = useMatchStore((s) => s.status);
  const since = useMatchStore((s) => s.disconnectedAtMs);
  const online = useBrowserOnline();
  const wall = useNowMs();
  if (status === 'open') return online ? 'online' : 'offline';
  if (!online) return 'offline';
  if (status === 'connecting' || status === 'idle') return 'connecting';
  if (status === 'closed') return 'offline';
  return since !== null && wall - since > OFFLINE_AFTER_MS ? 'offline' : 'reconnecting';
}
